import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login, clearError } from '../../redux/slices/authSlice';
import { authService } from '../../services/api';
import { APP_VERSION } from '../../version';
import BiztimizeMark from '../../components/common/BiztimizeMark';

const LoginSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

const getTenantName = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'Biztimize';
  }
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const subdomain = parts[0];
    if (!['www', 'localhost', '127'].includes(subdomain)) {
      return subdomain.toUpperCase();
    }
  }
  return 'Biztimize';
};

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [tenantName, setTenantName] = useState('');
  const [tenantLogo, setTenantLogo] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isLoading, error, token, user } = useSelector((state) => state.auth);

  useEffect(() => {
    setTenantName(getTenantName());
  }, []);

  // Tenant branding is decoration, never a gate: a tenant with no logo, an
  // unreachable endpoint and a broken image file all render the card exactly as
  // it did before there was a logo at all.
  useEffect(() => {
    let cancelled = false;
    authService
      .getTenantBranding()
      .then((response) => {
        if (!cancelled) {
          setTenantLogo(response.data?.logo_url || null);
        }
      })
      .catch(() => {
        /* no logo: the sign-in form must still work */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (token) {
      if (user?.must_change_password) {
        navigate('/auth/change-password');
      } else {
        navigate('/');
      }
    }
  }, [token, user, navigate]);

  const handleSubmit = async (values) => {
    const result = await dispatch(login(values));
    if (result.meta.requestStatus === 'fulfilled') {
      const payload = result.payload || {};
      // Conditional MFA: route to setup (first time) or the code challenge.
      if (payload.mfaRequired) {
        navigate(payload.setupRequired ? '/auth/mfa-setup' : '/auth/mfa');
        return;
      }
      if (payload.user?.must_change_password) {
        navigate('/auth/change-password');
      } else {
        navigate('/');
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Panel - Branding */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 45%',
            bgcolor: '#2C3E50',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 8,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.04,
              backgroundImage:
                'radial-gradient(circle at 25% 25%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 75%, #fff 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: { md: 120, lg: 148 }, height: { md: 120, lg: 148 }, mb: 1.5 }}>
              <BiztimizeMark size="100%" />
            </Box>
            <Typography
              sx={{
                fontSize: { md: '3.5rem', lg: '4.5rem' },
                fontWeight: 700,
                color: '#EEF2F7',
                lineHeight: 1,
                letterSpacing: '-0.025em',
                mb: 2,
              }}
            >
              Biztimize
            </Typography>
            <Typography
              sx={{
                fontSize: { md: '1rem', lg: '1.15rem' },
                color: '#B7C1CC',
                fontWeight: 400,
                maxWidth: 420,
              }}
            >
              Optimize your Business
            </Typography>
          </Box>

          {/* Footer on left panel */}
          <Typography
            sx={{
              position: 'absolute',
              bottom: 32,
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.3)',
            }}
          >
            &copy; {new Date().getFullYear()} Biztimize. All rights reserved. &nbsp;v{APP_VERSION}
          </Typography>
        </Box>
      )}

      {/* Right Panel - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#EEF2F7',
          px: { xs: 3, sm: 6 },
          py: 4,
          position: 'relative',
        }}
      >
        {/* Mobile branding */}
        {isMobile && (
          <Box
            sx={{
              textAlign: 'center',
              mb: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ mb: 1.5 }}>
              <BiztimizeMark size={64} tile />
            </Box>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#2C3E50',
                mb: 0.5,
              }}
            >
              Biztimize
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#5B6676' }}>
              Optimize your Business
            </Typography>
          </Box>
        )}

        {/* Login Card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            bgcolor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            p: { xs: 3.5, sm: 4.5 },
          }}
        >
          {tenantLogo && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
              <Box
                component="img"
                src={tenantLogo}
                alt={`${tenantName} logo`}
                onError={() => setTenantLogo(null)}
                sx={{
                  height: { xs: 64, sm: 72 },
                  maxWidth: '100%',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          )}

          <Typography
            sx={{
              fontSize: '1.6rem',
              fontWeight: 700,
              color: '#212529',
              textAlign: 'center',
              mb: 0.5,
            }}
          >
            Welcome to {tenantName}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: '#6c757d',
              textAlign: 'center',
              mb: 3.5,
            }}
          >
            Sign in to access your Account.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2.5, borderRadius: 1.5 }}
              onClose={() => dispatch(clearError())}
            >
              {typeof error === 'object'
                ? error.detail || error.message || error.messages || JSON.stringify(error)
                : error}
            </Alert>
          )}

          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Form>
                <TextField
                  fullWidth
                  id="username"
                  name="username"
                  label="Username"
                  variant="outlined"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                  disabled={isLoading}
                  autoComplete="username"
                  autoFocus
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#2C3E50' },
                      '&.Mui-focused fieldset': { borderColor: '#2C3E50' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#2C3E50' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px #fff inset',
                      WebkitTextFillColor: 'inherit',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  disabled={isLoading}
                  autoComplete="current-password"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#2C3E50' },
                      '&.Mui-focused fieldset': { borderColor: '#2C3E50' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#2C3E50' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px #fff inset',
                      WebkitTextFillColor: 'inherit',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                          sx={{ color: '#6c757d' }}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '28px',
                    bgcolor: '#2C3E50',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#1A242F',
                      boxShadow: '0 4px 12px rgba(44,62,80,0.3)',
                    },
                    '&:disabled': {
                      bgcolor: '#2C3E50',
                      opacity: 0.7,
                      color: '#fff',
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                  <Link to="/auth/forgot-password" style={{ textDecoration: 'none' }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#3498db',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Forgot Password?
                    </Typography>
                  </Link>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                  <Typography
                    sx={{ fontSize: '0.8rem', color: '#6c757d' }}
                  >
                    Need help?{' '}
                    <Box
                      component="a"
                      href="mailto:support@biztimize.com"
                      sx={{
                        color: '#2C3E50',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Contact Support
                    </Box>
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>

        {/* Mobile footer */}
        {isMobile && (
          <Typography
            sx={{ mt: 4, fontSize: '0.8rem', color: '#adb5bd' }}
          >
            &copy; {new Date().getFullYear()} Biztimize. All rights reserved. &nbsp;v{APP_VERSION}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LoginPage;
